import { useEffect, useState } from "react";
import { DatepickerRange } from "../../utils/daterange";
import { DisplayCard } from "../whatsapp/datadisplay";
import { CircularLoading } from "../utils/loading";
import { sendServerRequest } from "../../server/request";
import { URLS } from "../../env/urls";
import { openGlobalSnack } from "../utils/snack";
import { LoadingButton } from "@mui/lab";
import { TableWithSearchAndSortEngine } from "../utils/table";
import { formatDateWithMin } from "../../utils/date";

// Uma linha de quebra (count agrupado por uma coluna).
interface GroupCount {
  value: string;
  count: number;
  label?: string;
}
interface Analytics {
  total: number;
  retried: number;
  eligibleRetry131049: number;
  byStatus: GroupCount[];
  byTemplate: GroupCount[];
  byScript: GroupCount[];
  bySource: GroupCount[];
  byStartState: GroupCount[];
  byNumber: GroupCount[];
}
interface JobRow {
  id: number;
  status: string;
  template_name: string;
  chat_start_state: string;
  script_name: string;
  chat_source: string;
  wa_number_phone: string | null;
  tries: number;
  error_code?: number;
  error_description?: string;
  createdAt: string;
}

// Tabela genérica para uma quebra "valor -> total".
function BreakdownTable({ title, items, labelHeader }: { title: string; items: GroupCount[]; labelHeader?: string }) {
  return (
    <div style={{ display: 'inline-block', verticalAlign: 'top', margin: '10px', minWidth: '260px', textAlign: 'left' }}>
      <h5 style={{ textAlign: 'center', marginBottom: '8px' }}>{title}</h5>
      {items.length < 1 ? <p style={{ textAlign: 'center' }}>Sem dados.</p> :
        <TableWithSearchAndSortEngine
          header={[
            { label: labelHeader || 'Valor', id: 'value', showValue: (e: GroupCount) => e.label || e.value, allowSort: true },
            { label: 'Total', id: 'count', allowSort: true },
          ]}
          items={items}
        />}
    </div>
  );
}

export function TemplateJobs() {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [rows, setRows] = useState<JobRow[]>([]);
  const [retrying, setRetrying] = useState(false);

  // Dispara o redisparo dos shots com erro 131049 (sem inverter o número). O
  // backend processa em background; recarregamos o analítico ao final.
  function retry131049() {
    setRetrying(true);
    sendServerRequest('POST', URLS.templateJobs.retry131049, {}).then(r => {
      const d = r.data.data;
      openGlobalSnack(d.info || `Redisparo iniciado: ${d.matched} shot(s).`, d.matched > 0 ? 'success' : 'info');
      setRetrying(false);
      setLoading(true);
    }).catch(() => {
      openGlobalSnack('Erro ao iniciar o redisparo.', 'error');
      setRetrying(false);
    });
  }

  useEffect(() => {
    if (!loading) return;
    const body = {
      fromDate: new Date(startDate).setHours(12, 0, 0, 0),
      toDate: new Date(endDate).setHours(12, 0, 0, 0),
    };
    Promise.all([
      sendServerRequest('POST', URLS.templateJobs.analytics, body),
      sendServerRequest('POST', URLS.templateJobs.list, { ...body, page: 1, pageSize: 100 }),
    ]).then(([a, l]) => {
      setAnalytics(a.data.data);
      setRows(l.data.data.list);
      setLoading(false);
    }).catch(() => {
      openGlobalSnack('Erro ao carregar os disparos de template.', 'error');
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <div style={{ textAlign: 'center' }}>
      <h5>Disparos de Template (WhatsApp)</h5>
      <DatepickerRange onUpdated={(s, e) => { setStartDate(s); setEndDate(e); }} />
      <LoadingButton loading={loading} onClick={() => setLoading(true)} sx={{ marginTop: '5px' }} variant='contained'>
        Atualizar
      </LoadingButton>

      {loading ? <CircularLoading text="Carregando..." /> : analytics &&
        <div>
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <DisplayCard primary="Total de Disparos" secondary={`${analytics.total}`} />
            <DisplayCard primary="Com Reenvio" secondary={`${analytics.retried}`} marginLeft="15px"
              toolTip="Disparos que precisaram de mais de 1 tentativa (tries > 1)." />
            <DisplayCard primary="Falha 131049" secondary={`${analytics.eligibleRetry131049}`} marginLeft="15px"
              toolTip="Shots com status 'failed', erro 131049 e tries ≤ 10 — elegíveis para redisparo." />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <LoadingButton
              loading={retrying}
              disabled={analytics.eligibleRetry131049 < 1}
              onClick={retry131049}
              color="warning"
              variant='contained'
            >
              Redisparar erro 131049 ({analytics.eligibleRetry131049})
            </LoadingButton>
          </div>

          <div>
            <BreakdownTable title="Por Status" items={analytics.byStatus} labelHeader="Status" />
            <BreakdownTable title="Por Template" items={analytics.byTemplate} labelHeader="Template" />
            <BreakdownTable title="Por Script" items={analytics.byScript} labelHeader="Script" />
            <BreakdownTable title="Por Origem" items={analytics.bySource} labelHeader="Origem" />
            <BreakdownTable title="Por Estado Inicial" items={analytics.byStartState} labelHeader="Estado" />
            <BreakdownTable title="Por Número" items={analytics.byNumber} labelHeader="Número" />
          </div>

          <div style={{ marginTop: '30px' }}>
            <h5>Detalhes (últimos 100)</h5>
            {rows.length < 1 ? <p>Sem disparos neste período.</p> :
              <TableWithSearchAndSortEngine
                header={[
                  { label: 'ID', id: 'id', allowSort: true },
                  { label: 'Status', id: 'status', allowSort: true },
                  { label: 'Template', id: 'template_name', allowSort: true },
                  { label: 'Script', id: 'script_name' },
                  { label: 'Origem', id: 'chat_source' },
                  { label: 'Estado Inicial', id: 'chat_start_state' },
                  { label: 'Número', id: 'wa_number_phone' },
                  { label: 'Tentativas', id: 'tries', allowSort: true },
                  { label: 'Erro', id: 'error_description', showValue: (e: JobRow) => e.error_description || '-' },
                  { label: 'Data', id: 'createdAt', showValue: (e: JobRow) => formatDateWithMin(e.createdAt), allowSort: true },
                ]}
                items={rows}
              />}
          </div>
        </div>}
    </div>
  );
}
